import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AgentLeaveService, AgentAttendanceDayDto, AgentLeaveQuotaDto, ApplyMultipleLeavesDto } from '../../../services/agent-leave.service';
import { AgentService, AgentDto } from '../../../services/agent.service';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-agent-attendance-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './agent-attendance-calendar.component.html',
  styleUrl: './agent-attendance-calendar.component.css'
})
export class AgentAttendanceCalendarComponent implements OnInit {
  agentId = '';
  selectedAgentId = '';
  agent: AgentDto | null = null;
  agents: AgentDto[] = [];
  quota: AgentLeaveQuotaDto | null = null;
  attendanceDays: AgentAttendanceDayDto[] = [];
  calendarDays: (AgentAttendanceDayDto | null)[] = [];
  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Current calendar view date
  currentYear = new Date().getFullYear();
  currentMonth = new Date().getMonth() + 1; // 1-indexed

  isLoading = false;
  isLoadingQuota = false;

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private leaveService: AgentLeaveService,
    private agentService: AgentService
  ) { }

  ngOnInit(): void {
    this.loadAgents();
    
    this.route.paramMap.subscribe(params => {
      const routeAgentId = params.get('agentId');
      if (routeAgentId) {
        this.agentId = routeAgentId;
        this.selectedAgentId = routeAgentId;
        this.loadAgentInfo();
        this.loadQuotaInfo();
        this.loadAttendance();
      } else {
        this.agentId = '';
        this.selectedAgentId = '';
        this.agent = null;
        this.quota = null;
        this.attendanceDays = [];
        this.calendarDays = [];
      }
    });
  }

  loadAgents(): void {
    this.agentService.getAgentList(undefined, true, 0, 100).subscribe({
      next: (res) => {
        this.agents = res.items || [];
      },
      error: (err) => {
        console.error('Failed to load agents list', err);
      }
    });
  }

  onAgentChange(): void {
    if (this.selectedAgentId) {
      this.router.navigate(['/agents/attendance', this.selectedAgentId]);
    } else {
      this.router.navigate(['/agents/attendance']);
    }
  }

  loadAgentInfo(): void {
    this.agentService.getAgentById(this.agentId).subscribe({
      next: (agent: AgentDto) => {
        this.agent = agent;
      },
      error: () => {
        this.showNotification('Failed to load agent profile.', 'error');
      }
    });
  }

  loadQuotaInfo(): void {
    this.isLoadingQuota = true;
    this.leaveService.getAgentQuota(this.agentId).subscribe({
      next: (res) => {
        this.quota = res;
        this.isLoadingQuota = false;
      },
      error: (err) => {
        console.error('Failed to load quota info', err);
        this.showNotification('Failed to load leave quota info.', 'error');
        this.isLoadingQuota = false;
      }
    });
  }

  loadAttendance(): void {
    this.isLoading = true;
    this.leaveService.getAgentAttendance(this.agentId, this.currentYear, this.currentMonth).subscribe({
      next: (res) => {
        this.attendanceDays = res;
        this.generateCalendarGrid();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load attendance', err);
        this.showNotification('Failed to load attendance calendar data.', 'error');
        this.isLoading = false;
      }
    });
  }

  generateCalendarGrid(): void {
    this.calendarDays = [];
    if (!this.attendanceDays || this.attendanceDays.length === 0) return;

    // Day of the week for the 1st of the month (0 = Sun, 1 = Mon, etc.)
    const firstDayIndex = new Date(this.currentYear, this.currentMonth - 1, 1).getDay();

    // Pad beginning of calendar with nulls
    for (let i = 0; i < firstDayIndex; i++) {
      this.calendarDays.push(null);
    }

    // Push actual day dtos
    for (const day of this.attendanceDays) {
      this.calendarDays.push(day);
    }
  }

  prevMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 1) {
      this.currentMonth = 12;
      this.currentYear--;
    }
    this.loadAttendance();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 12) {
      this.currentMonth = 1;
      this.currentYear++;
    }
    this.loadAttendance();
  }

  get monthName(): string {
    return this.getMonthName(this.currentMonth);
  }

  getMonthName(monthNum: number): string {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1];
  }

  get presentCount(): number {
    return this.attendanceDays.reduce((acc, d) => acc + (d.presentWeight || 0), 0);
  }

  get plannedLeaveCount(): number {
    return this.attendanceDays.reduce((acc, d) => acc + (d.plannedLeaveWeight || 0), 0);
  }

  get unplannedLeaveCount(): number {
    return this.attendanceDays.reduce((acc, d) => acc + (d.unplannedLeaveWeight || 0), 0);
  }

  get shadowUnplannedLeaveCount(): number {
    return this.unplannedLeaveCount;
  }

  get holidayCount(): number {
    return this.attendanceDays.filter(d => d.status === 'Holiday').length;
  }

  // Mark Leave Modal Properties
  showMarkLeaveModal = false;
  leaveDate = '';
  leaveIsHalfDay = false;
  leaveReason = '';
  leaveIsUnplanned = false;
  submittingLeave = false;
  selectedDates: string[] = [];

  toggleDateSelection(dateStr: string): void {
    const dateObj = new Date(dateStr);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const index = this.selectedDates.indexOf(formattedDate);
    if (index > -1) {
      this.selectedDates.splice(index, 1);
    } else {
      this.selectedDates.push(formattedDate);
      this.selectedDates.sort();
    }
  }

  isDateSelected(dateStr: string): boolean {
    const dateObj = new Date(dateStr);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    return this.selectedDates.includes(formattedDate);
  }

  clearSelection(): void {
    this.selectedDates = [];
  }

  openMarkLeaveModal(dateStr?: string): void {
    if (dateStr) {
      const dateObj = new Date(dateStr);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      this.selectedDates = [formattedDate];
      this.leaveDate = formattedDate;
    } else if (this.selectedDates.length === 1) {
      this.leaveDate = this.selectedDates[0];
    } else {
      this.leaveDate = '';
    }
    this.leaveIsHalfDay = false;
    this.leaveReason = '';
    this.leaveIsUnplanned = false;
    this.showMarkLeaveModal = true;
  }

  closeMarkLeaveModal(): void {
    this.showMarkLeaveModal = false;
    this.leaveDate = '';
    this.leaveIsHalfDay = false;
    this.leaveReason = '';
    this.leaveIsUnplanned = false;
    this.submittingLeave = false;
  }

  submitMarkLeave(): void {
    if (!this.leaveDate && this.selectedDates.length === 0) {
      this.showNotification('Date is required.', 'error');
      return;
    }
    if (!this.leaveReason.trim()) {
      this.showNotification('Reason is required.', 'error');
      return;
    }

    this.submittingLeave = true;

    if (this.selectedDates.length > 0) {
      const dto: ApplyMultipleLeavesDto = {
        dates: this.selectedDates,
        isHalfDay: this.leaveIsHalfDay,
        reason: this.leaveReason.trim(),
        isUnplanned: this.leaveIsUnplanned
      };

      this.leaveService.applyMultipleLeaves(this.agentId, dto).subscribe({
        next: () => {
          this.showNotification('Leaves marked and approved successfully.', 'success');
          this.clearSelection();
          this.closeMarkLeaveModal();
          this.loadAttendance();
          this.loadQuotaInfo();
        },
        error: (err) => {
          console.error('Failed to mark leaves', err);
          this.showNotification(err.error?.message || 'Failed to mark leaves.', 'error');
          this.submittingLeave = false;
        }
      });
    } else {
      const dto = {
        date: this.leaveDate,
        isHalfDay: this.leaveIsHalfDay,
        reason: this.leaveReason.trim(),
        isUnplanned: this.leaveIsUnplanned
      };

      this.leaveService.applyLeaveForAgent(this.agentId, dto).subscribe({
        next: () => {
          this.showNotification('Leave marked and approved successfully.', 'success');
          this.closeMarkLeaveModal();
          this.loadAttendance();
          this.loadQuotaInfo();
        },
        error: (err) => {
          console.error('Failed to mark leave', err);
          this.showNotification(err.error?.message || 'Failed to mark leave.', 'error');
          this.submittingLeave = false;
        }
      });
    }
  }

  isCellClickable(day: AgentAttendanceDayDto | null): boolean {
    if (!day) return false;
    return day.status === 'Future' || day.status === 'OffDuty';
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = {
      show: true,
      message,
      type
    };
    setTimeout(() => {
      this.notification.show = false;
    }, 4000);
  }
}
